import { useEffect } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useLogMeInD0to6 } from '@/components/confluence/hooks/use-log-me-in-d0to6.tsx';
import { useLoggedOut } from '@/components/user-context/helpers/cohorts';
import { wacToken } from '@/libraries/design/foundations';
import { getLocalizedImage } from '@/localization/localizable-images/paid-lp-conf/localized-images-conf-features';
import ImageChecklistSide from '@/patterns/image-checklist-side/src/image-checklist-side';
import { PaidHeroCarousel } from '@/patterns/paid-hero/src/paid-hero-carousel';
import {
	PaidLandingConfProps,
	PaidLandingConfTemplate,
} from '@/templates/paid-landing/src/paid-conf.template';
import HeroSection from '@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero';
import { BanditsProvider } from '@/utils/anchor-queries/bandits-components.tsx';
import { AnchorContentsResponse } from '@/utils/anchor-queries/get-anchor-contents';
import { BANDITS_PRODUCT } from '@/utils/anchor-queries/get-bandits-contents';
import { experiments } from '@/utils/experiment/manifest';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { useLocation } from '@/utils/location-provider';
import { useEnglishLocale } from '@/utils/sentry-intl';
import { Bundle } from '@/utils/urls';

import { ConfluenceSoftwarePageProps } from './confluence-software-page';
import { isTargetedSubAnchor } from './subAnchors/subAnchor-util.tsx';
export interface ConfluencePagePaidLpConfFeaturesProps extends ConfluenceSoftwarePageProps {
	anchorContents?: AnchorContentsResponse | null;
	persona?: string;
	subAnchor?: string;
}

export const ConfluenceSoftwarePageVariantPaidLpConfFeatures = (
	pageProps: ConfluencePagePaidLpConfFeaturesProps,
	// eslint-disable-next-line complexity
) => {
	const intl = useIntl();
	const [exposureSubAnchorFired, fireSubAnchorExposure] = useExperimentExposure(
		experiments.cfind4782LandingPageSubAnchorV3,
	);

	// Evaluate condition for Conf BPS with Bandits
	const isLoggedOut = useLoggedOut();
	const isEnglishLocale = useEnglishLocale();
	const locationSearch = useLocation().search;

	useEffect(() => {
		if (isTargetedSubAnchor(pageProps.subAnchor) && !exposureSubAnchorFired) {
			fireSubAnchorExposure();
		}
	}, [exposureSubAnchorFired, fireSubAnchorExposure, pageProps.subAnchor]);

	const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6({
		availableProducts: pageProps.availableProducts,
		userProfile: pageProps.userProfile,
	});

	const gwp9286ConfluenceAnchorUpdates = checkFeatureFlagGate(
		featureFlags.gwp9286ConfluenceAnchorUpdates,
	);

	const carouselLiftAndShift = [
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSection.start-with-page.title',
				defaultMessage: 'Start with a page',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSection.start-with-page.body',
				defaultMessage: 'Plan projects, take notes, and brainstorm. Let AI write your first draft.',
			}),
			// will fix in separate ticket GWP-4659

			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/cs8c8dbwh841g64nad3y8o63fo6peqkv.webp',
			imgAltText: 'A Confluence page being edited',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSection.share-with-teams.title',
				defaultMessage: 'Share with your teams',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSection.share-with-teams.body',
				defaultMessage:
					'Invite your teams to collaborate using real-time editing and inline comments.',
			}),
			// will fix in separate ticket GWP-4659

			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/63i2v45spmjd7q66mqefep624mf6y20h.webp',
			imgAltText: 'A Confluence page with multiple teammates editing and leaving comments',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSection.connect-work.title',
				defaultMessage: 'Connect all your work',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSection.connect-work.body',
				defaultMessage: 'Stay organized in dedicated workspaces, equipped with AI-powered search.',
			}),
			// will fix in separate ticket GWP-4659

			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/o47q33qpw6cp3211df81a3noi34e41ib.webp',
			imgAltText: 'A Confluence page with a summary being generated by Atlassian Intelligence',
		},
	];

	const carouselSlidesGwp9286Updates = [
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSectionGwp9286Updates.create-more-together.title',
				defaultMessage: 'Create more together',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSectionGwp9286Updates.create-more-together.body',
				defaultMessage: 'Instantly draft and brainstorm with Rovo AI and plan for any project',
			}),
			imgSrc:
				'https://dam-cdn.atl.orangelogic.com/AssetLink/l8pf65qmp34as5x0u4d57k7x7hq82k45/Anchor_Journey_Confluence_Brand_Carousel_1.webp',
			imgAltText: 'Create more together',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSectionGwp9286Updates.collaborate-across-teams.title',
				defaultMessage: 'Collaborate across teams',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSectionGwp9286Updates.collaborate-across-teams.body',
				defaultMessage: 'Move projects forward faster with human-AI collaboration',
			}),
			imgSrc:
				'https://dam-cdn.atl.orangelogic.com/AssetLink/sfr1x0ctcr3k8w56256gb5787tta8s8t/Anchor_Journey_Confluence_Brand_Carousel_2.webp',
			imgAltText: 'Collaborate across teams',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSectionGwp9286Updates.connect-knowledge-from-anywhere.title',
				defaultMessage: 'Connect knowledge from anywhere',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.FeaturesSectionGwp9286Updates.connect-knowledge-from-anywhere.body',
				defaultMessage: 'Get relevant answers across all your tools with AI-powered search',
			}),
			imgSrc:
				'https://dam-cdn.atl.orangelogic.com/AssetLink/krl10r0n7l1x62114173sia1ws136md4/Anchor_Journey_Confluence_Brand_Carousel_3.webp',
			imgAltText: 'Connect knowledge from anywhere',
		},
	];

	const carouselSlides = gwp9286ConfluenceAnchorUpdates
		? carouselSlidesGwp9286Updates
		: carouselLiftAndShift;

	const showDefault = !shouldShowLogMeIn;

	let customHeroSection = (
		<PaidHeroCarousel
			pageId={showDefault ? 'confluence-default' : 'paid-lp-conf-features'}
			embeddedSignup={{ bundle: 'confluence' as Bundle }}
			carouselSlides={carouselSlides}
			keyword={pageProps.keyword}
			usePlainContainer={gwp9286ConfluenceAnchorUpdates}
		/>
	);

	if (shouldShowLogMeIn) {
		customHeroSection = (
			<FeatureExposed experimentKey="cfind-2801-get-paid-users-back-to-product" group="Test">
				<HeroSection
					confluenceSites={recentSites}
					userProfileName={userProfileName}
					hideExploreMore
					loggedInPaid
				/>
			</FeatureExposed>
		);
	}

	const props: PaidLandingConfProps = {
		customHeroSection,
		heroProps: {
			headerText: intl.formatMessage({
				id: 'ConfluenceSoftwarePagePaidLpConfFeatures.heroHeader',
				defaultMessage: 'Accomplish more together with Confluence',
			}),
			secondaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePagePaidLpConfFeatures.heroBody',
				defaultMessage: 'So many features, so little time — all built to meet your team’s needs',
			}),
			featuresLabel: intl.formatMessage({
				id: 'ConfluenceSoftwarePagePaidLpConfFeatures.heroListHeading',
				defaultMessage: 'What you get on the free plan:',
			}),
			featuresList: [
				intl.formatMessage({
					id: 'ConfluenceSoftwarePagePaidLpConfFeatures.heroListItem1',
					defaultMessage: 'Supports up to 10 users or 3 agents',
				}),
				intl.formatMessage({
					id: 'ConfluenceSoftwarePagePaidLpConfFeatures.heroListItem2',
					defaultMessage: 'Includes 2 GB of storage',
				}),
				intl.formatMessage({
					id: 'ConfluenceSoftwarePagePaidLpConfFeatures.heroListItem3',
					defaultMessage: 'Offers Community support',
				}),
				intl.formatMessage({
					id: 'ConfluenceSoftwarePagePaidLpConfFeatures.heroListItem4',
					defaultMessage: 'Is always free, no credit card needed',
				}),
			],
			signup: {
				bundle: 'confluence',
				edition: 'free',
			},
		},
		tabsProps: {
			dataProductKey: 'confluence-PaidLpConflFeatures',
			headerText: gwp9286ConfluenceAnchorUpdates
				? intl.formatMessage({
						id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.tabsHeader',
						defaultMessage: 'Power every stage of work with Confluence',
					})
				: intl.formatMessage({
						id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsHeader',
						defaultMessage:
							'Jump start company-wide collaboration with fewer tools by using Confluence.',
					}),
			tabStyles: {
				// eslint-disable-next-line local/ensure-wac-token-usage
				defaultBG: '#ebecf0',
				// eslint-disable-next-line local/ensure-wac-token-usage
				defaultText: '#42526e',
				// eslint-disable-next-line local/ensure-wac-token-usage
				activeBG: '#0747a6',
				activeText: wacToken('color.neutral.light0'),
				// eslint-disable-next-line local/ensure-wac-token-usage
				hoverBG: '#ebecf0',
				// eslint-disable-next-line local/ensure-wac-token-usage
				hoverText: '#42526e',
			},
			styles: {
				'--lp-tabs-hover': '#c1c7d0',
				'--lp-tabs-unselected': '#6b778c',
			},
			tabs: gwp9286ConfluenceAnchorUpdates
				? [
						{
							textLabel: intl.formatMessage({
								id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.pill.create',
								defaultMessage: 'Create',
							}),
							id: 'tab-create',
							component: CreateGwp9286(),
						},
						{
							textLabel: intl.formatMessage({
								id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.pill.transform-work',
								defaultMessage: 'Transform work',
							}),
							id: 'tab-transform-work',
							component: TransformWorkGwp9286(),
						},
						{
							textLabel: intl.formatMessage({
								id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.pill.stay-up-to-date',
								defaultMessage: 'Stay up to date',
							}),
							id: 'tab-stay-up-to-date',
							component: StayUpToDateGwp9286(),
						},
						{
							textLabel: intl.formatMessage({
								id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.pill.get-answers',
								defaultMessage: 'Get answers',
							}),
							id: 'tab-get-answers',
							component: GetAnswersGwp9286(),
						},
					]
				: [
						{
							textLabel: intl.formatMessage({
								id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.pill.documentation',
								defaultMessage: 'Documentation',
							}),
							id: 'tab-documentation',
							component: Documentation(),
						},
						{
							textLabel: intl.formatMessage({
								id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.pill.collaboration',
								defaultMessage: 'Collaboration',
							}),
							id: 'tab-collaboration',
							component: Collaboration(),
						},
						{
							textLabel: intl.formatMessage({
								id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.pill.truth',
								defaultMessage: 'Single source of truth',
							}),
							id: 'tab-truth',
							component: Truth(),
						},
						{
							textLabel: intl.formatMessage({
								id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.pill.templates',
								defaultMessage: 'Templates',
							}),
							id: 'tab-templates',
							component: Templates(),
						},
					],
		},
		keyword: pageProps.keyword,
		subAnchor: pageProps.subAnchor,
	};

	if (showDefault) {
		props.testimonialProps = undefined;
	}

	const isBanditsEnabled = isLoggedOut && isEnglishLocale && locationSearch.includes('keyword');

	return (
		<BanditsProvider
			product={BANDITS_PRODUCT.CONFLUENCE}
			isExperimentEnabled={isBanditsEnabled}
			keyword={pageProps.keyword}
			subAnchor={pageProps.subAnchor || ''}
			channel={pageProps.utmMedium || 'paid-search'}
			utmContent={pageProps.utmContent || ''}
			utmCampaign={pageProps.utmCampaign || ''}
		>
			<PaidLandingConfTemplate {...props} />
		</BanditsProvider>
	);
};

/* GWP-9286 Updated Tabs */

const CreateGwp9286 = () => {
	const intl = useIntl();

	const title = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.create.heading',
		defaultMessage: 'Put pen to paper without the heavy lifting',
	});

	const listItems = [
		<FormattedMessage
			key="listItem1"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.create.listItem1"
			defaultMessage="Beat the blank page with AI-powered creation that brings your ideas to life in seconds."
		/>,
		<FormattedMessage
			key="listItem2"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.create.listItem2"
			defaultMessage="Start fast with pre-built templates for meeting notes, project plans, retros, and more."
		/>,
		<FormattedMessage
			key="listItem3"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.create.listItem3"
			defaultMessage="Articulate your ideas in a variety of content types such as pages, whiteboards, and databases."
		/>,
	];

	const imageSrc =
		'https://dam-cdn.atl.orangelogic.com/AssetLink/k71m1n07jgo4dqk526dw11nhw4ia441p/Anchor_Journey_Confluence_Brand_Tab_1.webp';
	const imageAlt = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.tabsImageAltText.create',
		defaultMessage: 'Create',
	});

	return (
		<ImageChecklistSide
			title={title}
			listItems={listItems}
			imageSrc={imageSrc}
			imageAlt={imageAlt}
		/>
	);
};

const TransformWorkGwp9286 = () => {
	const intl = useIntl();

	const title = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.transform-work.heading',
		defaultMessage: 'Transform ideas into action',
	});

	const listItems = [
		<FormattedMessage
			key="listItem1"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.transform-work.listItem1"
			defaultMessage="Turn brainstorms into structured plans with AI that organizes thoughts for you."
		/>,
		<FormattedMessage
			key="listItem2"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.transform-work.listItem2"
			defaultMessage="Push work forward with seamlessly integrations with tools like Jira and other 3rd party apps"
		/>,
		<FormattedMessage
			key="listItem3"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.transform-work.listItem3"
			defaultMessage="Collaborate with teammates in real-time on flexible content types like whiteboards, pages, and databases."
		/>,
	];

	const imageSrc =
		'https://dam-cdn.atl.orangelogic.com/AssetLink/12kbaa12oil2751c30185853j6413vb2/Anchor_Journey_Confluence_Brand_Tab_2.webp';
	const imageAlt = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.tabsImageAltText.transform-work',
		defaultMessage: 'Transform work',
	});

	return (
		<ImageChecklistSide
			title={title}
			listItems={listItems}
			imageSrc={imageSrc}
			imageAlt={imageAlt}
		/>
	);
};

const StayUpToDateGwp9286 = () => {
	const intl = useIntl();

	const title = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.stay-up-to-date.heading',
		defaultMessage: 'Stay in the know, your way',
	});

	const listItems = [
		<FormattedMessage
			key="listItem1"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.stay-up-to-date.listItem1"
			defaultMessage="AI summaries and audio briefings make it quick and easy to stay caught up on all things work."
		/>,
		<FormattedMessage
			key="listItem2"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.stay-up-to-date.listItem2"
			defaultMessage="Get notified when content gets updated or when teammates share new information."
		/>,
		<FormattedMessage
			key="listItem3"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.stay-up-to-date.listItem3"
			defaultMessage="Stay up to date on work happening across your org with personalized feeds."
		/>,
	];

	const imageSrc =
		'https://dam-cdn.atl.orangelogic.com/AssetLink/3u2u80i2k4wnm266fqmtgnemy6d1ljt3/Anchor_Journey_Confluence_Brand_Tab_3.webp';
	const imageAlt = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.tabsImageAltText.stay-up-to-date',
		defaultMessage: 'Stay up to date',
	});

	return (
		<ImageChecklistSide
			title={title}
			listItems={listItems}
			imageSrc={imageSrc}
			imageAlt={imageAlt}
		/>
	);
};

const GetAnswersGwp9286 = () => {
	const intl = useIntl();

	const title = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.get-answers.heading',
		defaultMessage: 'Get context and answers in seconds',
	});

	const listItems = [
		<FormattedMessage
			key="listItem1"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.get-answers.listItem1"
			defaultMessage="Find exactly what you need with AI-powered search that understands context and company jargon."
		/>,
		<FormattedMessage
			key="listItem2"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.get-answers.listItem2"
			defaultMessage="Organize work into workspaces that centralize key information and break down siloes."
		/>,
		<FormattedMessage
			key="listItem3"
			id="ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.get-answers.listItem3"
			defaultMessage="Spend less time digging and more time doing with connected knowledge across teams."
		/>,
	];

	const imageSrc =
		'https://dam-cdn.atl.orangelogic.com/AssetLink/4648h612p8824q33utwoa73nr08717n5/Anchor_Journey_Confluence_Brand_Tab_4.webp';
	const imageAlt = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeaturesGwp9286Updates.tabSection.tabsImageAltText.get-answers',
		defaultMessage: 'Get answers',
	});

	return (
		<ImageChecklistSide
			title={title}
			listItems={listItems}
			imageSrc={imageSrc}
			imageAlt={imageAlt}
		/>
	);
};

/* Tabs */

const Documentation = () => {
	const intl = useIntl();

	const title = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsHeader.documentation',
		defaultMessage: 'Create better documentation for your teams',
	});

	const listItems = [
		<FormattedMessage
			key="listItem1"
			id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.documentation.listItem1"
			defaultMessage="Organize pages, attachments, and even spaces with keyword labels — such as meeting type or project name."
		/>,
		<FormattedMessage
			key="listItem2"
			id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.documentation.listItem2"
			defaultMessage="Use the advanced search & page tree to quickly find and label key docs in a well-organized page tree."
		/>,
		<FormattedMessage
			key="listItem3"
			id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.documentation.listItem3"
			defaultMessage="Use the Table of Contents macro to help colleagues quickly locate information."
		/>,
	];

	const imageSrc = getLocalizedImage('documentation', intl.locale);
	const imageAlt = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsImageAltText.documentation',
		defaultMessage: 'Documentation',
	});

	return (
		<ImageChecklistSide
			title={title}
			listItems={listItems}
			imageSrc={imageSrc}
			imageAlt={imageAlt}
		/>
	);
};

const Collaboration = () => {
	const intl = useIntl();

	const title = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsHeader.collaboration',
		defaultMessage: 'Collaborate with teams in real-time',
	});

	const listItems = [
		<FormattedMessage
			key="listItem1"
			id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.collaboration.listItem1"
			defaultMessage="Co-edit together in real time and publish updates that highlight changes."
		/>,
		<FormattedMessage
			key="listItem2"
			id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.collaboration.listItem2"
			defaultMessage="Make it a “team effort” with in-line and page comments, likes, and visual elements, such as images, GIFs, and emojis."
		/>,
		<FormattedMessage
			key="listItem3"
			id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.collaboration.listItem3"
			defaultMessage="Alert your teammates when you tag them or assign a task so everyone stays on top of progress."
		/>,
	];

	const imageSrc = getLocalizedImage('collaboration', intl.locale);
	const imageAlt = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsImageAltText.collaboration',
		defaultMessage: 'Project collaboration',
	});

	return (
		<ImageChecklistSide
			title={title}
			listItems={listItems}
			imageSrc={imageSrc}
			imageAlt={imageAlt}
		/>
	);
};

const Truth = () => {
	const intl = useIntl();

	const title = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsHeader.truth',
		defaultMessage: 'Share information between teams',
	});

	const listItems = [
		<FormattedMessage
			key="listItem1"
			id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.truth.listItem1"
			defaultMessage="Keep employees informed with open access to information they need, but share and protect sensitive content with permission settings."
		/>,
		<FormattedMessage
			key="listItem2"
			id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.truth.listItem2"
			defaultMessage="Easily get back to recent spaces, in-progress work, and drafts, and stay updated with activity and popular feeds on your personal “homepage.”"
		/>,
	];

	const imageSrc = getLocalizedImage('truth', intl.locale);
	const imageAlt = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsImageAltText.truth',
		defaultMessage: 'Program manager hub',
	});

	return (
		<ImageChecklistSide
			title={title}
			listItems={listItems}
			imageSrc={imageSrc}
			imageAlt={imageAlt}
		/>
	);
};

const Templates = () => {
	const intl = useIntl();

	const title = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsHeader.templates',
		defaultMessage: 'There’s a template for that',
	});

	const listItems = [
		<FormattedMessage
			key="listItem1"
			id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.templates.listItem1"
			defaultMessage="Use best-practices templates, from product roadmaps to marketing plans so you can quickly jump right in."
		/>,
		<FormattedMessage
			key="listItem2"
			id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.templates.listItem2"
			defaultMessage="Kickstart the creation stage by spending less time formatting pages and more time getting sh*t done."
		/>,
		<FormattedMessage
			key="listItem3"
			id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.templates.listItem3"
			defaultMessage="Start with a blank page or choose from over 75 customizable templates for every team–from strategy and planning docs to reports."
		/>,
	];

	const imageSrc = getLocalizedImage('templates', intl.locale);
	const imageAlt = intl.formatMessage({
		id: 'ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsImageAltText.templates',
		defaultMessage: 'Confluence templates',
	});

	return (
		<ImageChecklistSide
			title={title}
			listItems={listItems}
			imageSrc={imageSrc}
			imageAlt={imageAlt}
		/>
	);
};
